import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useProjectVariables } from '../../hooks/useProjectVariables';
import { ROUTES } from '../../routes';

interface ProjectVariablesPanelProps {
  projectId: string;
}

type FormMode = { type: 'closed' } | { type: 'create' };

export function ProjectVariablesPanel({ projectId }: ProjectVariablesPanelProps) {
  const {
    variables,
    loading,
    upgradeRequired,
    fetchProjectVariables,
    createProjectVariable,
    updateProjectVariable,
    deleteProjectVariable,
  } = useProjectVariables(projectId);

  const [formMode, setFormMode] = useState<FormMode>({ type: 'closed' });
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editKey, setEditKey] = useState('');
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProjectVariables();
  }, [fetchProjectVariables]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKey.trim()) return;
    setSaving(true);
    try {
      let parsedValue: unknown = newValue;
      try {
        parsedValue = JSON.parse(newValue);
      } catch {
        // keep as string
      }
      await createProjectVariable(newKey.trim(), parsedValue);
      setNewKey('');
      setNewValue('');
      setFormMode({ type: 'closed' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id: string) => {
    setSaving(true);
    try {
      let parsedValue: unknown = editValue;
      try {
        parsedValue = JSON.parse(editValue);
      } catch {
        // keep as string
      }
      await updateProjectVariable(id, editKey.trim(), parsedValue);
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (v: { id: string; key: string; value: unknown }) => {
    setEditingId(v.id);
    setEditKey(v.key);
    setEditValue(typeof v.value === 'string' ? v.value : JSON.stringify(v.value));
  };

  if (upgradeRequired) {
    return (
      <div className="card bg-base-100 mb-6">
        <div className="px-4 py-3 flex items-center justify-between">
          <div>
            <h2 className="text-base-content">Project Variables</h2>
            <p className="text-xs text-base-content/70">
              Share variables across all endpoints in this project via <code className="bg-base-300 px-1 rounded-field text-xs">{'{{project.name}}'}</code>
            </p>
          </div>
          <Link to={ROUTES.pricing()} className="btn btn-primary btn-sm">Upgrade to Pro</Link>
        </div>
      </div>
    );
  }

  if (loading && variables.length === 0) {
    return (
      <div className="card bg-base-100 mb-6">
        <div className="px-4 py-3 border-b border-base-200">
          <h2 className="text-base-content">Project Variables</h2>
        </div>
        <div className="px-4 py-12 text-center text-base-content/70">
          Loading variables...
        </div>
      </div>
    );
  }

  if (variables.length === 0 && formMode.type === 'closed') {
    return (
      <div className="card bg-base-100 mb-6">
        <div className="px-4 py-3 flex items-center justify-between">
          <div>
            <h2 className="text-base-content">Project Variables</h2>
            <p className="text-xs text-base-content/70">
              Shared across all endpoints via <code className="bg-base-300 px-1 rounded-field text-xs">{'{{project.name}}'}</code>
            </p>
          </div>
          <button
            onClick={() => setFormMode({ type: 'create' })}
            className="btn btn-primary btn-sm"
          >
            Add Variable
          </button>
        </div>
      </div>
    );
  }

  const formArea = (
    <form onSubmit={handleCreate} className="p-4 flex gap-2 items-end">
      <div className="fieldset flex-1">
        <label className="fieldset-legend">
          Key
        </label>
        <input
          type="text"
          className="input input-sm"
          placeholder="e.g. base_url"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          required
        />
      </div>
      <div className="fieldset flex-1">
        <label className="fieldset-legend">
          Value
        </label>
        <input
          type="text"
          className="input input-sm"
          placeholder="e.g. https://api.example.com"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
        />
      </div>
      <button
        type="submit"
        className="btn btn-primary btn-sm"
        disabled={saving || !newKey.trim()}
      >
        Add Variable
      </button>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => setFormMode({ type: 'closed' })}
      >
        Cancel
      </button>
    </form>
  );

  return (
    <div className="card bg-base-100 mb-6">
      <div className="px-4 py-3 border-b border-base-200 flex items-center justify-between">
        <div>
          <h2 className="text-base-content">Project Variables</h2>
          <p className="text-xs text-base-content/70">
            Shared across all endpoints via <code className="bg-base-300 px-1 rounded-field text-xs">{'{{project.name}}'}</code>
          </p>
        </div>
        {formMode.type === 'closed' && (
          <button
            onClick={() => setFormMode({ type: 'create' })}
            className="btn btn-primary btn-sm"
          >
            Add Variable
          </button>
        )}
      </div>

      {formMode.type === 'create' && (
        <div className={variables.length > 0 ? 'border-b border-base-200' : ''}>
          {formArea}
        </div>
      )}

      {variables.length > 0 && (
        <div className="overflow-x-auto">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Key</th>
                <th>Value</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {variables.map((v) => (
                <tr key={v.id}>
                  {editingId === v.id ? (
                    <>
                      <td>
                        <input
                          type="text"
                          className="input input-sm w-full"
                          value={editKey}
                          onChange={(e) => setEditKey(e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="input input-sm w-full"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                        />
                      </td>
                      <td>
                        <div className="flex gap-1">
                          <button
                            className="btn btn-ghost btn-xs"
                            onClick={() => handleUpdate(v.id)}
                            disabled={saving}
                          >
                            Save
                          </button>
                          <button
                            className="btn btn-ghost btn-xs"
                            onClick={() => setEditingId(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="font-mono text-sm">{v.key}</td>
                      <td className="text-sm max-w-xs truncate" title={typeof v.value === 'string' ? v.value : JSON.stringify(v.value)}>
                        {typeof v.value === 'string' ? v.value : JSON.stringify(v.value)}
                      </td>
                      <td>
                        <div className="flex gap-1">
                          <button
                            className="btn btn-ghost btn-xs"
                            onClick={() => startEditing(v)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-ghost btn-xs text-error"
                            onClick={() => deleteProjectVariable(v.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
